import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { SalaryTypeFormRule, SalaryTypeFormSchema } from "@core/form/form.rule";
import { useFormDefaultSalaryType } from "@core/form/form-default-salary-type.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateSalaryType, 
  useQyGetSalaryTypeById, 
  useQyDeleteSalaryType 
} from "@core/query/salary-type.query";
import { GetResponseSalaryTypeModel } from "@core/model/query-model-salary-type";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditSalaryType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultSalaryType();
  const navigate = useNavigate();
  const { mapUpdateSalaryType, mapDeleteSalaryType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { salaryTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID SalaryType
  const handleGetApiSuccess = (data: GetResponseSalaryTypeModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: salaryTypeResponse,
    isLoading,
    isError: salaryTypeError,
  } = useQyGetSalaryTypeById(salaryTypeId || "", handleGetApiSuccess);
  const salaryTypeData = salaryTypeResponse?.data?.[0];

  // API UPDATE SalaryType
  const handleUpdateApiSuccess = () => {
    showSuccess("SalaryType updated successfully");
  };
  const { mutate: updateSalaryType, isLoading: isEditLoading } = useQyUpdateSalaryType(
    handleUpdateApiSuccess
  );

  // API DELETE SalaryType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("SalaryType deleted successfully");
  };
  const { mutate: deleteSalaryType } = useQyDeleteSalaryType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<SalaryTypeFormSchema>({
    defaultValues: getDefault(salaryTypeData),
    resolver: zodResolver(SalaryTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: SalaryTypeFormSchema) => {
    if (!salaryTypeData) {
      throw new Error("No salaryType data");
    }

    const formData = mapUpdateSalaryType(form, salaryTypeData.code);
    updateSalaryType(formData);
  };
  const handleValidateError = (err: FieldErrors<SalaryTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!salaryTypeData) {
      throw new Error("No salaryType data found");
    }

    const form = mapDeleteSalaryType(salaryTypeData.code);
    deleteSalaryType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    salaryTypeId,
    salaryTypeData,
    isLoading,
    salaryTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}